import type { ContactFieldDto } from '@carbonwebmail/contracts'
import { useState } from 'react'
import { FilterActions } from '../../ui/FilterActions.js'
import { Popover } from '../../ui/Popover.js'
import type { Rule } from '../segments/ruleVocabulary.js'
import {
  applyPatch,
  fieldOptions,
  fieldPatch,
  fieldValue,
  kindOf,
  OPERATORS,
  VALUELESS,
  VALUES,
} from '../segments/ruleVocabulary.js'
import type { KeyedRule } from '../segments/useSegmentBuilder.js'

/**
 * Narrowing the board to the people somebody is looking for.
 *
 * **These are segment rules, not a filter language of this screen's own.** The
 * same vocabulary, the same operators per field, and the same translation on
 * the server that decides who a campaign reaches — so a filter saved as a view
 * means on the board exactly what it will mean at send. A second language would
 * be a second answer to "who matches these rules", and the one that drifted
 * would be the one somebody had saved.
 *
 * A flat list of rules, all of which must hold. The segment builder has groups
 * and "any of", and this deliberately does not: a board filter is two or three
 * conditions somebody is narrowing with, and the screen for building a
 * complicated audience already exists. **Save as a view** hands what is here to
 * that screen rather than growing into it.
 *
 * An unfinished rule matches nobody, which the server also decides — it under
 * shows rather than quietly widening back to everybody.
 */

export function BoardFilter({
  fields,
  rules,
  onRules,
  onNewRule,
  onSaveAsView,
}: {
  readonly fields: readonly ContactFieldDto[]
  /**
   * Each rule with a key of its own, because they are edited in place and one
   * row has to be tellable from another. The keys never leave the board.
   */
  readonly rules: readonly KeyedRule[]
  readonly onRules: (rules: readonly KeyedRule[]) => void
  readonly onNewRule: (rule: Rule) => KeyedRule
  /**
   * Opens the save dialog, which keeps what is on screen — the filter, the order
   * and the hidden columns. It takes no argument: the rules it would name are
   * the ones the board already holds, and passing them from here would be a
   * second copy of what a view is made of.
   */
  readonly onSaveAsView: () => void
}) {
  const [open, setOpen] = useState(false)

  return (
    <Popover
      label="Filter"
      onClose={() => setOpen(false)}
      onToggle={() => setOpen((was) => !was)}
      open={open}
      panelClassName="w-[26rem] p-3"
      trigger={rules.length === 0 ? 'Filter' : `Filter (${rules.length})`}
      triggerClassName={
        rules.length === 0
          ? 'rounded-field px-3 py-1.5 font-medium text-[13px] text-t2 hover:bg-line-2 hover:text-t1'
          : 'rounded-field bg-brand-soft px-3 py-1.5 font-medium text-[13px] text-brand-strong'
      }
    >
      <p className="mb-2 font-semibold text-[12px] text-t2 uppercase tracking-[0.06em]">
        Show contacts where
      </p>

      {rules.map((keyed) => (
        <RuleRow
          fields={fields}
          key={keyed.key}
          onChange={(patch) =>
            onRules(
              rules.map((one) =>
                one.key === keyed.key ? { ...one, rule: applyPatch(one.rule, patch) } : one,
              ),
            )
          }
          onRemove={() => onRules(rules.filter((one) => one.key !== keyed.key))}
          rule={keyed.rule}
        />
      ))}

      <FilterActions
        count={rules.length}
        onAdd={() =>
          onRules([...rules, onNewRule({ field: 'status', operator: 'is', value: 'subscribed' })])
        }
        onClear={() => onRules([])}
        onSaveAsView={onSaveAsView}
      />
    </Popover>
  )
}

function RuleRow({
  rule,
  fields,
  onChange,
  onRemove,
}: {
  readonly rule: Rule
  readonly fields: readonly ContactFieldDto[]
  readonly onChange: (patch: Partial<Rule>) => void
  readonly onRemove: () => void
}) {
  const kind = kindOf(rule, fields)
  const choices = VALUES[fieldValue(rule)]

  return (
    <span className="mb-2 flex flex-wrap items-center gap-1">
      <select
        aria-label="Column"
        className={FIELD}
        onChange={(event) => onChange(fieldPatch(event.target.value))}
        value={fieldValue(rule)}
      >
        {fieldOptions(fields).map(([value, label]) => (
          <option key={value} value={value}>
            {label}
          </option>
        ))}
      </select>

      <select
        aria-label="Condition"
        className={FIELD}
        onChange={(event) => onChange({ operator: event.target.value as Rule['operator'] })}
        value={rule.operator}
      >
        {OPERATORS[kind].map(([value, label]) => (
          <option key={value} value={value}>
            {label}
          </option>
        ))}
      </select>

      {/* Presence questions have no value to give, so none is asked for. */}
      {VALUELESS.includes(rule.operator) ? null : choices === undefined ? (
        <input
          aria-label="Value"
          className={FIELD}
          onChange={(event) => onChange({ value: event.target.value })}
          type={kind === 'number' || kind === 'days' ? 'number' : 'text'}
          value={rule.value}
        />
      ) : (
        <select
          aria-label="Value"
          className={FIELD}
          onChange={(event) => onChange({ value: event.target.value })}
          value={rule.value}
        >
          {choices.map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
      )}

      <button
        aria-label="Remove this condition"
        className="h-6 w-6 flex-none rounded-field text-[13px] text-t3 hover:bg-line-2 hover:text-danger"
        onClick={onRemove}
        type="button"
      >
        ×
      </button>
    </span>
  )
}

const FIELD =
  'min-w-0 flex-1 rounded-field border border-line bg-surface px-2 py-1.5 text-[12px] text-t1'
